import { LogoHeader } from '@/components/molecules';
import { useTranslation, useUIColors } from '@/core/hooks';
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Platform, StyleSheet, View } from 'react-native';
import { useUAEPass } from '../hooks/useUAEPass';
import { uaePassService } from '../services/uaePassService';
import { UAEPassAuthResult } from '../types';
import { UAEPassWebView } from './UAEPassWebView';

function toUserFacingApiError(message: string | undefined, fallback: string): string {
    const raw = (message ?? '').trim();
    const isTechnical =
        raw === 'ANDROID_HTTP_400' ||
        /status code\s*\d+|request failed|axios/i.test(raw);
    return !raw || isTechnical ? fallback : raw;
}

export interface UAEPassAuthProps {
    onAuthSuccess: (result: UAEPassAuthResult) => void;
    onAuthError: (error: string) => void;
    onClose: () => void;
    hideWebView?: boolean; // Hide WebView when showing error dialog
    initialCode?: string | null;
    initialState?: string | null;
    initialUrl?: string | null;
}

export const UAEPassAuth: React.FC<UAEPassAuthProps> = ({
    onAuthSuccess,
    onAuthError,
    onClose,
    hideWebView = false,
    initialCode,
    initialState,
    initialUrl,
}) => {
    const [authUrl, setAuthUrl] = useState<string | null>(null);
    const [isProcessingCallback, setIsProcessingCallback] = useState(false);
    const { authenticate, error } = useUAEPass();
    const { t } = useTranslation();

    useEffect(() => {
        if (error) {
            onAuthError(error);
        }
    }, [error, onAuthError]);

    useEffect(() => {
        const initializeAuth = async () => {
            if (initialCode && initialState) {
                // Direct callback via deep link code/state
                handleCallbackData(initialCode, initialState);
                return;
            }
            if (initialUrl) {
                setAuthUrl(initialUrl);
                return;
            }
            try {
                const result = await authenticate();
                if (result && result.authUrl) {
                    setAuthUrl(result.authUrl);
                } else if (result && !result.success) {
                    onAuthError(result.error || 'Authentication failed');
                }
            } catch (err) {
                onAuthError(err instanceof Error ? err.message : 'Authentication failed');
            }
        };

        initializeAuth();
    }, [authenticate, initialCode, initialState, initialUrl, onAuthError]);

    /**
     * Handle callback data from WebView or deep link
     * Makes API call to backend with code and state
     */
    const handleCallbackData = async (code: string, state: string) => {
        if (isProcessingCallback) return; // Prevent duplicate calls

        try {
            setIsProcessingCallback(true);
            const result: UAEPassAuthResult = await uaePassService.handleCallback(code, state);
            if (result.success) {
                // Close WebView before proceeding
                setAuthUrl(null);
                onAuthSuccess(result);
            } else {
                // Close WebView and surface error via onAuthError (parent will handle navigation)
                setAuthUrl(null);
                onAuthError(toUserFacingApiError(result.error, t('errors.registrationFailed')));
            }
        } catch (error) {
            // Close WebView and surface failure (parent will handle navigation)
            setAuthUrl(null);
            const rawMsg = error instanceof Error ? error.message : t('errors.registrationFailed');
            onAuthError(toUserFacingApiError(rawMsg, t('errors.registrationFailed')));
        } finally {
            setIsProcessingCallback(false);
        }
    };

    /**
     * Handle WebView authentication success (legacy - kept for compatibility)
     */
    const handleWebViewSuccess = (result: UAEPassAuthResult) => {
        onAuthSuccess(result);
    };

    /**
     * Handle WebView authentication error
     */
    const handleWebViewError = (error: string) => {
        onAuthError(error);
    };

    const handleCloseWebView = () => {
        setAuthUrl(null);
        onClose();
    };

    const colors = useUIColors();

    const showLoader = isProcessingCallback || (!authUrl && !hideWebView);
    const showWebView =
        !hideWebView &&
        Boolean(authUrl) &&
        !(Platform.OS === 'android' && isProcessingCallback);

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
            <LogoHeader onBackPress={onClose} />
            {showWebView && (
                <UAEPassWebView
                    authUrl={authUrl}
                    onAuthSuccess={handleWebViewSuccess}
                    onAuthError={handleWebViewError}
                    onClose={handleCloseWebView}
                    onCallbackData={handleCallbackData}
                />
            )}
            {showLoader && (
                <View style={styles.loadingContainer} pointerEvents="none">
                    <ActivityIndicator size="large" color={colors.bloomRed} />
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    loadingContainer: {
        position: 'absolute',
        top: 60,
        left: 0,
        right: 0,
        bottom: 0,
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10,
    },
});

export default UAEPassAuth;